import type { ReactNode } from 'react';
import { Checkbox, Col, Row, Typography } from 'antd';
import { groupLabel, groupPermissions, type GroupablePermission } from '../../permissions/grouping';

interface PickerPermission extends GroupablePermission {
  description?: string;
}

interface Props<T extends PickerPermission> {
  permissions: T[];
  /** Extra content after the key (e.g. a "Set in code" tag). */
  renderExtra?: (p: T) => ReactNode;
  isDisabled?: (p: T) => boolean;
}

// The checkboxes for a role's permissions: one heading per area and one box per group inside it.
// Render it inside the form's own Checkbox.Group, which holds the value.
export function PermissionPicker<T extends PickerPermission>({
  permissions,
  renderExtra,
  isDisabled,
}: Props<T>) {
  return (
    <Row gutter={[8, 16]}>
      {groupPermissions(permissions).map(({ category, groups }) => (
        <Col span={24} key={category}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 15,
              marginBottom: 8,
              textTransform: 'capitalize',
              color: '#262626',
            }}
          >
            {category}
          </div>
          {groups.map(({ group, items }) => {
            // An area with only one group needs no box of its own.
            const boxed = groups.length > 1;
            return (
              <section
                key={group}
                aria-label={boxed ? `${category} ${groupLabel(group)}` : undefined}
                style={
                  boxed
                    ? {
                        border: '1px solid #f0f0f0',
                        borderLeft: '3px solid #d9d9d9',
                        borderRadius: 6,
                        padding: '8px 12px',
                        marginBottom: 8,
                        background: '#fafafa',
                      }
                    : undefined
                }
              >
                {boxed && (
                  <Typography.Text
                    strong
                    style={{ display: 'block', marginBottom: 4, color: '#434343' }}
                  >
                    {groupLabel(group)} ({items.length})
                  </Typography.Text>
                )}
                <Row gutter={[8, 4]}>
                  {items.map((p) => (
                    <Col span={8} key={p.key}>
                      <Checkbox value={p.key} title={p.description} disabled={isDisabled?.(p)}>
                        {p.key}
                        {renderExtra?.(p)}
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </section>
            );
          })}
        </Col>
      ))}
    </Row>
  );
}
